if (!customElements.get('twt-board-review-carousel')) {
  const MAX_CHARS = 260;
  // Auto-advance pace. Long enough to read a ~260-character excerpt.
  const AUTOPLAY_MS = 6000;

  customElements.define(
    'twt-board-review-carousel',
    class TwtBoardReviewCarousel extends HTMLElement {
      connectedCallback() {
        this.itemsParent = this.querySelector('[data-review-item]')?.parentElement;
        this.dotsParent = this.querySelector('.twt-board-overview__dots');
        this.starsEl = this.querySelector('[data-review-stars]');
        this.photoEl = this.querySelector('[data-review-photo]');
        if (this.photoEl) {
          this.defaultPhoto = { src: this.photoEl.currentSrc || this.photoEl.src, srcset: this.photoEl.getAttribute('srcset') };
        }
        this.prevButton = this.querySelector('[data-review-prev]');
        this.nextButton = this.querySelector('[data-review-next]');
        this.prevButton?.addEventListener('click', () => this.step(-1, true));
        this.nextButton?.addEventListener('click', () => this.step(1, true));
        this.collectNodes();
        this.bind();
        this.setupAutoplay();
        this.loadProductReviews();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.visibilityObserver?.disconnect();
      }

      // Move by one review, wrapping around. A click restarts the countdown so the
      // review the shopper just chose stays up for a full interval.
      step(direction, fromUser) {
        if (this.items.length < 2) return;
        this.show((this.index + direction + this.items.length) % this.items.length);
        if (fromUser) this.restartAutoplay();
      }

      // Rotates every AUTOPLAY_MS while the carousel is on screen. Pauses on hover and
      // keyboard focus, and in a background tab; stops for good once a review is
      // expanded; never starts when the visitor asked for reduced motion.
      setupAutoplay() {
        this.autoplayStopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.paused = false;
        this.onScreen = false;
        this.addEventListener('mouseenter', () => this.pause(true));
        this.addEventListener('mouseleave', () => this.pause(false));
        this.addEventListener('focusin', () => this.pause(true));
        this.addEventListener('focusout', () => this.pause(false));
        document.addEventListener('visibilitychange', () => this.restartAutoplay());
        if ('IntersectionObserver' in window) {
          this.visibilityObserver = new IntersectionObserver((entries) => {
            this.onScreen = entries.some((entry) => entry.isIntersecting);
            this.restartAutoplay();
          });
          this.visibilityObserver.observe(this);
        } else {
          this.onScreen = true;
          this.restartAutoplay();
        }
      }

      pause(paused) {
        this.paused = paused;
        this.restartAutoplay();
      }

      stopAutoplay() {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      restartAutoplay() {
        this.stopAutoplay();
        if (this.autoplayStopped || this.paused || !this.onScreen || document.hidden) return;
        if (!this.items || this.items.length < 2) return;
        this.autoplayTimer = window.setInterval(() => this.step(1, false), AUTOPLAY_MS);
      }

      collectNodes() {
        this.items = Array.from(this.querySelectorAll('[data-review-item]'));
        this.dots = Array.from(this.querySelectorAll('[data-review-dot]'));
        this.index = 0;
        const single = this.items.length < 2;
        if (this.prevButton) this.prevButton.hidden = single;
        if (this.nextButton) this.nextButton.hidden = single;
      }

      bind() {
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => {
            this.show(i);
            this.restartAutoplay();
          });
        });

        this.querySelectorAll('[data-review-more]').forEach((button) => {
          button.addEventListener('click', (event) => this.expand(event.currentTarget));
        });

        if (!this.touchBound) {
          this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
          this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
          this.touchBound = true;
        }
      }

      expand(button) {
        const item = button.closest('[data-review-item]');
        const textEl = item?.querySelector('[data-review-text]');
        const full = item?.dataset.reviewFull;
        if (!textEl || !full) return;
        textEl.textContent = full;
        button.remove();
        // The shopper is reading: do not swap the review out from under them.
        this.autoplayStopped = true;
        this.stopAutoplay();
      }

      onTouchStart(event) {
        this.touchStartX = event.changedTouches[0].screenX;
      }

      onTouchEnd(event) {
        if (this.items.length < 2) return;
        const delta = event.changedTouches[0].screenX - this.touchStartX;
        if (Math.abs(delta) < 40) return;
        this.step(delta < 0 ? 1 : -1, true);
      }

      show(index) {
        this.index = index;
        this.items.forEach((item, i) => {
          item.hidden = i !== index;
        });
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
        this.setStars(parseFloat(this.items[index]?.dataset.reviewScore));
        this.setPhoto(this.items[index]?.dataset.reviewPhoto);
      }

      // A customer photo when the visible review has one; the board's photo otherwise.
      setPhoto(url) {
        if (!this.photoEl || !this.defaultPhoto) return;
        if (url) {
          this.photoEl.removeAttribute('srcset');
          this.photoEl.src = url;
        } else {
          if (this.defaultPhoto.srcset) this.photoEl.setAttribute('srcset', this.defaultPhoto.srcset);
          this.photoEl.src = this.defaultPhoto.src;
        }
      }

      // Each review carries its own rating, so the stars must follow the visible review
      // rather than showing one blended average.
      setStars(score) {
        if (!this.starsEl || !score) return;
        const rounded = Math.max(1, Math.min(5, Math.round(score)));
        this.starsEl.textContent = '★'.repeat(rounded) + '☆'.repeat(5 - rounded);
        this.starsEl.setAttribute('aria-label', `${rounded} stars`);
      }

      // Judge.me pools reviews across grouped products, so pull the widget feed and keep
      // only the reviews actually written for this product.
      loadProductReviews() {
        const { shopDomain, productId, jdgmToken, productHandle } = this.dataset;
        this.locale = (this.dataset.locale || '').toLowerCase();
        this.verifiedLabel = this.dataset.verifiedLabel || 'Verified buyer';
        if (!shopDomain || !productId || !jdgmToken || !productHandle) return;

        const start = () => {
          // Token goes in the query string, not an X-Api-Token header: Judge.me answers the
          // CORS preflight with a 404, so any custom header makes the browser block the request.
          const pages = [1, 2, 3].map((page) => {
            const url =
              `https://api.judge.me/api/v1/widgets/product_review?shop_domain=${encodeURIComponent(shopDomain)}` +
              `&external_id=${encodeURIComponent(productId)}&per_page=50&page=${page}` +
              `&api_token=${encodeURIComponent(jdgmToken)}`;
            return fetch(url)
              .then((response) => (response.ok ? response.json() : null))
              .catch(() => null);
          });

          Promise.all(pages)
            .then((results) => this.renderMatches(this.extractMatches(results, productHandle)))
            .catch(() => {});
        };

        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              observer.disconnect();
              start();
            },
            { rootMargin: '400px' }
          );
          observer.observe(this);
        } else {
          start();
        }
      }

      extractMatches(results, productHandle) {
        const parser = new DOMParser();
        const seen = new Set();
        const matches = [];

        results.forEach((result) => {
          if (!result || !result.widget) return;
          const doc = parser.parseFromString(result.widget, 'text/html');
          doc.querySelectorAll('.jdgm-rev').forEach((node) => {
            const url = node.getAttribute('data-product-url') || '';
            if (!url.endsWith(`/products/${productHandle}`)) return;

            const id = node.getAttribute('data-review-id') || '';
            if (id && seen.has(id)) return;
            seen.add(id);

            const body = node.querySelector('.jdgm-rev__body')?.textContent.trim();
            if (!body) return;

            const pic = node.querySelector('.jdgm-rev__pics img, .jdgm-rev__pic-img');
            const photo = pic ? pic.getAttribute('data-src') || pic.getAttribute('src') || '' : '';

            matches.push({
              body: body,
              photo: /^https?:|^\/\//.test(photo) ? photo : '',
              author: node.querySelector('.jdgm-rev__author')?.textContent.trim() || this.verifiedLabel,
              language: (node.getAttribute('data-review-language') || '').toLowerCase(),
              score: parseFloat(node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')) || 0,
            });
          });
        });

        // Reviews are never translated, only ordered: on /fr the ones written in French come
        // first, and the board's English reviews still fill in when it has fewer than three.
        if (this.locale) {
          matches.sort((a, b) => (b.language === this.locale) - (a.language === this.locale));
        }
        return matches.slice(0, 3);
      }

      // Cut on a word boundary so the visible excerpt never ends mid-word.
      shorten(text) {
        if (text.length <= MAX_CHARS) return text;
        const slice = text.slice(0, MAX_CHARS);
        const lastSpace = slice.lastIndexOf(' ');
        return (lastSpace > MAX_CHARS * 0.6 ? slice.slice(0, lastSpace) : slice).trimEnd();
      }

      renderMatches(matches) {
        if (!matches.length || !this.itemsParent || !this.dotsParent) return;

        this.items.forEach((item) => item.remove());
        this.dotsParent.textContent = '';

        matches.forEach((match, i) => {
          const item = document.createElement('div');
          item.setAttribute('data-review-item', '');
          item.dataset.reviewScore = match.score;
          item.dataset.reviewFull = match.body;
          if (match.photo) item.dataset.reviewPhoto = match.photo;
          item.hidden = i !== 0;

          const quote = document.createElement('blockquote');
          const text = document.createElement('span');
          text.setAttribute('data-review-text', '');
          const short = this.shorten(match.body);
          text.textContent = short;
          quote.append(text);

          if (short.length < match.body.length) {
            const more = document.createElement('button');
            more.type = 'button';
            more.setAttribute('data-review-more', '');
            more.setAttribute('aria-label', 'Read the full review');
            more.textContent = '…';
            quote.append(more);
          }

          const cite = document.createElement('cite');
          cite.textContent = `– ${match.author}`;

          item.append(quote, cite);
          this.itemsParent.append(item);

          const dot = document.createElement('i');
          dot.setAttribute('data-review-dot', '');
          if (i === 0) dot.className = 'is-active';
          this.dotsParent.append(dot);
        });

        this.collectNodes();
        this.bind();
        this.setStars(matches[0].score);
        this.setPhoto(matches[0].photo);
        this.restartAutoplay();
      }
    }
  );
}
